/**
 * Chart controller — owns everything the chart canvas needs and renders one
 * complete frame on demand.
 *
 * It lives outside React entirely. Pan, zoom, crosshair movement and drawing
 * manipulation all mutate plain fields here and set a repaint flag; the shared
 * frame loop picks it up on the next vsync. Dragging a chart therefore costs
 * zero React renders, which is the only way a pointermove at 120Hz stays
 * smooth while eight symbols are streaming.
 *
 * `dirty()` is the other half of that bargain: an untouched chart with no new
 * candles repaints only when the bar-close countdown ticks over, so an idle
 * terminal is genuinely idle.
 */

import { clamp } from '../lib/num';
import { store } from '../data/store';
import type { Interval } from '../data/binance';
import type { KlineSeries } from '../data/klines';
import {
  IndicatorCache,
  createIndicator,
  type IndicatorConfig,
  type IndicatorKind,
  type IndicatorOutput,
} from '../data/indicators';
import { alertEngine } from '../data/alerts';
import { C, F } from '../styles/palette';
import { crisp, type CanvasSurface } from '../lib/frame';
import { price as fmtPrice } from '../lib/num';
import { ChartViewport } from './scales';
import { ChartRenderer, DEFAULT_THEME, type ChartTheme, type HoverState } from './renderer';
import { DrawingLayer, type DrawTool, type HitContext } from './drawings';
import { drawOverlayLegend, drawPriceOverlays, drawSubPane, priceExtent, subPaneRect } from './overlays';

type DragMode = 'none' | 'pan' | 'scaleY' | 'scaleX';

const INDICATORS_KEY = 'terminal.indicators.v1';

export class ChartController {
  readonly vp = new ChartViewport();
  readonly renderer = new ChartRenderer();
  readonly drawings = new DrawingLayer();
  private cache = new IndicatorCache();

  indicators: IndicatorConfig[] = [];
  theme: ChartTheme = { ...DEFAULT_THEME };

  hover: HoverState = { active: false, x: 0, y: 0, index: -1 };

  private drag: DragMode = 'none';
  private dragX = 0;
  private dragY = 0;
  private shift = false;
  private needsPaint = true;

  private lastSeq = -1;
  private lastBookSeq = -1;
  private lastDrawVersion = -1;
  private lastSecond = -1;
  private lastSymbol = '';
  private lastInterval = '';
  private lastWidth = 0;
  private lastHeight = 0;

  private outputs: IndicatorOutput[] = [];
  private historyPending = false;

  /** Called when something changed that React chrome should reflect. */
  onChange: (() => void) | null = null;

  constructor() {
    this.indicators = this.loadIndicators();
  }

  // -- indicator config -----------------------------------------------------

  private loadIndicators(): IndicatorConfig[] {
    try {
      const raw = localStorage.getItem(INDICATORS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as IndicatorConfig[];
        if (Array.isArray(parsed)) return parsed.filter((i) => i && i.kind && i.id);
      }
    } catch {
      /* ignore */
    }
    // A sane default deck rather than an empty chart.
    const ema = createIndicator('ema', 1);
    ema.params.period = 21;
    const bb = createIndicator('bb', 2);
    const rsi = createIndicator('rsi', 0);
    return [ema, bb, rsi];
  }

  private saveIndicators(): void {
    try {
      localStorage.setItem(INDICATORS_KEY, JSON.stringify(this.indicators));
    } catch {
      /* quota */
    }
  }

  addIndicator(kind: IndicatorKind): void {
    this.indicators = [...this.indicators, createIndicator(kind, this.indicators.length)];
    this.saveIndicators();
    this.invalidate();
  }

  removeIndicator(id: string): void {
    this.indicators = this.indicators.filter((i) => i.id !== id);
    this.cache.drop(id);
    this.saveIndicators();
    this.invalidate();
  }

  toggleIndicator(id: string): void {
    this.indicators = this.indicators.map((i) =>
      i.id === id ? { ...i, visible: !i.visible } : i,
    );
    this.saveIndicators();
    this.invalidate();
  }

  updateIndicator(id: string, params: Record<string, number>): void {
    this.indicators = this.indicators.map((i) =>
      i.id === id ? { ...i, params: { ...i.params, ...params } } : i,
    );
    this.saveIndicators();
    this.invalidate();
  }

  setIndicatorColor(id: string, color: string): void {
    this.indicators = this.indicators.map((i) => (i.id === id ? { ...i, color } : i));
    this.saveIndicators();
    this.invalidate();
  }

  // -- view controls --------------------------------------------------------

  setStyle(style: ChartTheme['candleStyle']): void {
    this.theme = { ...this.theme, candleStyle: style };
    this.invalidate();
  }

  toggleGrid(): void {
    this.theme = { ...this.theme, showGrid: !this.theme.showGrid };
    this.invalidate();
  }

  toggleVolume(): void {
    this.theme = { ...this.theme, showVolume: !this.theme.showVolume };
    this.invalidate();
  }

  toggleLog(): void {
    this.vp.logScale = !this.vp.logScale;
    this.invalidate();
  }

  toggleMagnet(): void {
    this.drawings.magnet = !this.drawings.magnet;
    this.invalidate();
  }

  setTool(tool: DrawTool): void {
    this.drawings.setTool(tool);
    this.invalidate();
  }

  resetView(): void {
    this.vp.resetScale();
    this.vp.barsVisible = 140;
    this.vp.snapToRight(this.series().length);
    this.invalidate();
  }

  invalidate(): void {
    this.needsPaint = true;
    this.onChange?.();
  }

  private series(): KlineSeries {
    return store.activeSeries;
  }

  private hitCtx(): HitContext {
    return { vp: this.vp, series: this.series(), shift: this.shift };
  }

  // -- pointer input --------------------------------------------------------

  pointerDown(x: number, y: number, button: number, shift: boolean): void {
    this.shift = shift;
    this.needsPaint = true;

    // Price axis drag rescales the y range.
    if (x > this.vp.plotRight) {
      this.drag = 'scaleY';
      this.dragY = y;
      return;
    }
    // Time axis drag zooms the x range.
    if (y > this.vp.bodyBottom) {
      this.drag = 'scaleX';
      this.dragX = x;
      return;
    }

    if (this.drawings.pointerDown(x, y, this.hitCtx(), button)) {
      this.onChange?.();
      return;
    }

    this.drag = 'pan';
    this.dragX = x;
    this.dragY = y;
  }

  pointerMove(x: number, y: number, shift: boolean): void {
    this.shift = shift;
    const vp = this.vp;
    const series = this.series();

    switch (this.drag) {
      case 'pan': {
        const dx = x - this.dragX;
        const dy = y - this.dragY;
        this.dragX = x;
        this.dragY = y;
        if (dx !== 0) vp.panBy(dx, series.length);
        // Vertical drag only moves price when autoscale is already off; with
        // autoscale on it would fight the next frame's autoFit.
        if (dy !== 0 && !vp.autoScale) vp.shiftY(dy);
        this.maybeLoadHistory();
        this.needsPaint = true;
        break;
      }
      case 'scaleY': {
        const dy = y - this.dragY;
        this.dragY = y;
        if (vp.autoScale) {
          // Freeze the current auto range, then start scaling from it.
          vp.setManualRange(vp.priceMin, vp.priceMax);
        }
        vp.scaleY(1 + dy / 220);
        this.needsPaint = true;
        break;
      }
      case 'scaleX': {
        const dx = x - this.dragX;
        this.dragX = x;
        vp.zoomAt(vp.plotRight, 1 - dx / 320, series.length);
        this.needsPaint = true;
        break;
      }
      default: {
        if (this.drawings.pointerMove(x, y, this.hitCtx())) this.needsPaint = true;
      }
    }

    const index = Math.round(vp.indexAt(x));
    const inPlot = x >= vp.plotLeft && x <= vp.plotRight && y >= vp.bodyTop && y <= vp.bodyBottom;
    if (
      this.hover.x !== x ||
      this.hover.y !== y ||
      this.hover.active !== inPlot ||
      this.hover.index !== index
    ) {
      this.hover.x = x;
      this.hover.y = y;
      this.hover.active = inPlot;
      this.hover.index = index >= 0 && index < series.length ? index : -1;
      this.needsPaint = true;
    }
  }

  pointerUp(): void {
    if (this.drawings.pointerUp()) this.onChange?.();
    if (this.drag !== 'none') {
      this.drag = 'none';
      this.needsPaint = true;
    }
  }

  pointerLeave(): void {
    if (this.hover.active) {
      this.hover.active = false;
      this.hover.index = -1;
      this.needsPaint = true;
    }
    this.drag = 'none';
  }

  wheel(x: number, y: number, deltaY: number, deltaX: number, shift: boolean): void {
    const series = this.series();
    if (shift || Math.abs(deltaX) > Math.abs(deltaY)) {
      // Horizontal scroll pans instead of zooming.
      const d = (deltaX || deltaY) * 0.7;
      this.vp.panBy(-d, series.length);
      this.maybeLoadHistory();
    } else {
      // ~7% per notch; trackpads send many small deltas so scale by magnitude.
      const step = clamp(deltaY / 120, -3, 3);
      this.vp.zoomAt(x, Math.pow(1.14, step), series.length);
      this.maybeLoadHistory();
    }
    void y;
    this.needsPaint = true;
    this.onChange?.();
  }

  doubleClick(x: number): void {
    if (x > this.vp.plotRight) {
      this.vp.resetScale();
    } else {
      this.resetView();
    }
    this.needsPaint = true;
  }

  keyDown(key: string): boolean {
    if (this.drawings.keyDown(key)) {
      this.needsPaint = true;
      this.onChange?.();
      return true;
    }
    return false;
  }

  /**
   * Fetch older candles when the viewport approaches the left edge of what we
   * have. Guarded so a fast pan does not fire a dozen overlapping requests.
   */
  private maybeLoadHistory(): void {
    if (this.historyPending) return;
    const series = this.series();
    if (!series.loaded || series.historyExhausted || series.loadingHistory) return;
    if (this.vp.offset > 40) return;

    this.historyPending = true;
    const symbol = store.active;
    const interval = store.interval;
    void store
      .loadOlder(symbol, interval)
      .then((added) => {
        // Shift the viewport by exactly the number of bars prepended, or the
        // chart jumps backwards the instant history lands.
        if (added > 0 && store.active === symbol && store.interval === interval) {
          this.vp.offset += added;
          this.needsPaint = true;
        }
      })
      .finally(() => {
        this.historyPending = false;
      });
  }

  // -- frame ----------------------------------------------------------------

  dirty(): boolean {
    if (this.needsPaint) return true;
    const series = this.series();
    if (series.seq !== this.lastSeq) return true;
    if (store.active !== this.lastSymbol || store.interval !== this.lastInterval) return true;
    if (this.drawings.version !== this.lastDrawVersion) return true;
    const book = store.peek(store.active)?.book;
    if (book && book.seq !== this.lastBookSeq) return true;
    // The bar-close countdown changes once a second even when nothing else does.
    if (((performance.now() / 1000) | 0) !== this.lastSecond) return true;
    return false;
  }

  draw(surface: CanvasSurface): void {
    const { ctx, width, height } = surface;
    const vp = this.vp;
    const symbol = store.active;
    const interval = store.interval;
    const st = store.get(symbol);
    const series = st.series(interval);

    // Symbol change: rebind drawings and jump to the right edge.
    if (symbol !== this.lastSymbol) {
      this.drawings.setSymbol(symbol);
      vp.following = true;
      vp.resetScale();
    }
    if (interval !== this.lastInterval && this.lastInterval !== '') {
      vp.following = true;
      vp.resetScale();
    }

    const resized = width !== this.lastWidth || height !== this.lastHeight;
    vp.width = width;
    vp.height = height;

    // Sub-panes claim a share of the height proportional to how many there are.
    const subs = this.indicators.filter(
      (i) => i.visible && (i.kind === 'rsi' || i.kind === 'macd' || i.kind === 'atr'),
    );
    vp.subPaneFraction = subs.length === 0 ? 0 : clamp(subs.length * 0.19, 0, 0.55);
    vp.insets.bottom = 24;
    vp.insets.right = 76;

    this.renderer.clear(ctx, width, height);

    if (series.length === 0) {
      this.renderer.drawMessage(
        ctx,
        vp,
        series.loadingHistory || !series.loaded ? 'LOADING MARKET DATA…' : 'NO DATA',
      );
      this.commit(series, resized, symbol, interval);
      return;
    }

    if (vp.following || (resized && vp.isAtRight(series.length))) {
      vp.snapToRight(series.length);
    }
    vp.clamp(series.length);

    // Compute indicators before autoscale so overlays can widen the range.
    this.outputs = [];
    for (const cfg of this.indicators) {
      if (!cfg.visible) continue;
      this.outputs.push(this.cache.get(cfg, series));
    }
    const ext = priceExtent(vp, this.outputs, series.length);
    vp.autoFit(series, ext.min, ext.max);

    if (this.theme.showGrid) this.renderer.drawGrid(ctx, vp, series);

    // The price axis blanks the whole right-hand gutter before painting its
    // labels, so it has to run *before* any pane writes its own annotations
    // there. Drawing it last — as this did — silently erased the volume and
    // sub-pane axis labels and their crosshair tags every frame.
    this.renderer.drawPriceAxis(ctx, vp, st.pricePrecision);

    this.renderer.drawSessions(ctx, vp, series);
    if (this.theme.showVolume) this.renderer.drawVolume(ctx, vp, series, this.hover);

    drawPriceOverlays(ctx, vp, this.outputs, series.length);
    this.renderer.drawSeries(ctx, vp, series, this.theme);

    const subOutputs = this.outputs.filter((o) => o.pane === 'sub');
    for (let i = 0; i < subOutputs.length; i++) {
      drawSubPane(
        ctx,
        vp,
        subOutputs[i],
        subPaneRect(vp, i, subOutputs.length),
        series.length,
        this.hover,
      );
    }

    this.drawAlertLines(ctx, symbol, st.pricePrecision);
    this.drawings.render(ctx, this.hitCtx(), st.pricePrecision);

    this.renderer.drawTimeAxis(ctx, vp, series);
    this.renderer.drawQuotes(ctx, vp, st.ticker.bestBid, st.ticker.bestAsk);
    this.renderer.drawLastPrice(ctx, vp, series, st.pricePrecision);

    const legendY = this.renderer.drawLegend(
      ctx,
      vp,
      series,
      this.hover,
      st.pricePrecision,
      symbol,
      interval,
    );
    const idx = this.hover.active && this.hover.index >= 0 ? this.hover.index : series.length - 1;
    drawOverlayLegend(ctx, vp, this.outputs, idx, legendY);

    this.renderer.drawCrosshair(ctx, vp, series, this.hover, st.pricePrecision);
    this.renderer.drawClock(ctx, vp, st.ticker.updatedAt);

    const notes: string[] = [`${series.length} bars`];
    if (!vp.autoScale) notes.push('manual scale · dbl-click axis to reset');
    if (vp.logScale) notes.push('log');
    if (series.loadingHistory) notes.push('loading history…');
    if (this.drawings.isPlacing) notes.push('click to place · esc to cancel');
    this.renderer.drawFooter(ctx, vp, notes);

    this.commit(series, resized, symbol, interval);
  }

  /** Horizontal lines for every armed alert on this symbol. */
  private drawAlertLines(
    ctx: CanvasRenderingContext2D,
    symbol: string,
    precision: number,
  ): void {
    const alerts = alertEngine.forSymbol(symbol);
    if (alerts.length === 0) return;
    const vp = this.vp;

    ctx.save();
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = 1;
    ctx.font = F.micro;
    ctx.textBaseline = 'middle';

    for (const a of alerts) {
      const y = vp.yOf(a.price);
      if (y < vp.priceTop - 8 || y > vp.priceBottom + 8) continue;
      const col = a.status === 'triggered' ? C.text3 : a.direction === 'above' ? C.up : C.down;
      ctx.strokeStyle = col;
      ctx.globalAlpha = a.status === 'triggered' ? 0.35 : 0.8;
      ctx.beginPath();
      ctx.moveTo(vp.plotLeft, crisp(y));
      ctx.lineTo(vp.plotRight, crisp(y));
      ctx.stroke();

      ctx.globalAlpha = 1;
      const tag = `⌁ ${fmtPrice(a.price, precision)}`;
      const w = ctx.measureText(tag).width + 10;
      ctx.fillStyle = 'rgba(7,9,12,0.85)';
      ctx.fillRect(vp.plotLeft + 4, y - 7, w, 14);
      ctx.strokeStyle = col;
      ctx.setLineDash([]);
      ctx.strokeRect(crisp(vp.plotLeft + 4), crisp(y - 7), Math.round(w), 14);
      ctx.setLineDash([6, 4]);
      ctx.fillStyle = col;
      ctx.textAlign = 'left';
      ctx.fillText(tag, vp.plotLeft + 9, y + 0.5);
    }
    ctx.restore();
  }

  private commit(
    series: KlineSeries,
    resized: boolean,
    symbol: string,
    interval: Interval,
  ): void {
    this.needsPaint = false;
    this.lastSeq = series.seq;
    this.lastDrawVersion = this.drawings.version;
    this.lastBookSeq = store.peek(symbol)?.book.seq ?? -1;
    this.lastSecond = (performance.now() / 1000) | 0;
    this.lastSymbol = symbol;
    this.lastInterval = interval;
    this.lastWidth = this.vp.width;
    this.lastHeight = this.vp.height;
    void resized;
  }
}
